// react
import { ReactNode, useState } from 'react';

// i18n
import { useTranslation } from 'react-i18next';

// mui
import {
	alpha,
	Box,
	Button,
	Chip,
	IconButton,
	InputAdornment,
	Link,
	MenuItem,
	Popover,
	Select,
	Stack,
	TextField,
	Typography,
} from '@mui/material';

// icons
import {
	ChevronLeft as IconChevronLeft,
	ChevronRight as IconChevronRight,
	Close as IconClose,
	TuneRounded as IconTune,
	Search as IconSearch,
} from '@mui/icons-material';

export interface FilterOption {
	value: string | number;
	label: string;
}

export interface FilterConfig {
	key: string;
	label: string;
	options: FilterOption[];
	defaultValue?: string | number;
}

interface SearchFilterBarProps {
	searchPlaceholder?: string;
	searchValue: string;
	onSearchChange: (value: string) => void;
	filters: FilterConfig[];
	filterValues: Record<string, string | number>;
	onFilterChange: (key: string, value: string | number) => void;
	resultsCount?: number;
	totalCount?: number;
	actions?: ReactNode;
}

export const SearchFilterBar = ({
	searchPlaceholder = 'Search...',
	searchValue,
	onSearchChange,
	filters,
	filterValues,
	onFilterChange,
	resultsCount,
	totalCount,
	actions,
}: SearchFilterBarProps) => {
	const { t } = useTranslation();
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const open = Boolean(anchorEl);

	// Count active filters
	const activeFilterCount = filters.filter((filter) => {
		const currentValue = filterValues[filter.key];
		const firstOption = filter.options[0]?.value;
		return currentValue !== firstOption;
	}).length;

	// Get active filters for chips
	const activeFilters = filters
		.filter((filter) => {
			const currentValue = filterValues[filter.key];
			const firstOption = filter.options[0]?.value;
			return currentValue !== firstOption;
		})
		.map((filter) => {
			const currentValue = filterValues[filter.key];
			const option = filter.options.find((opt) => opt.value === currentValue);
			return {
				key: filter.key,
				label: filter.label,
				value: option?.label || String(currentValue),
				defaultValue: filter.options[0]?.value ?? '',
			};
		});

	const handleOpenFilters = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleCloseFilters = () => {
		setAnchorEl(null);
	};

	const handleClearFilter = (key: string, defaultValue: string | number) => {
		onFilterChange(key, defaultValue);
	};

	const handleClearAllFilters = () => {
		filters.forEach((filter) => {
			const defaultValue = filter.options[0]?.value ?? '';
			onFilterChange(filter.key, defaultValue);
		});
	};

	return (
		<Box>
			<Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
				{/* Search Field */}
				<TextField
					placeholder={searchPlaceholder}
					value={searchValue}
					onChange={(e) => onSearchChange(e.target.value)}
					size="small"
					sx={{
						minWidth: 240,
						flex: '1 1 240px',
						maxWidth: 320,
						'& .MuiOutlinedInput-root': {
							backgroundColor: 'background.paper',
							'& fieldset': {
								borderColor: 'divider',
							},
						},
					}}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<IconSearch sx={{ color: 'text.disabled', fontSize: 20 }} />
							</InputAdornment>
						),
						endAdornment: searchValue ? (
							<InputAdornment position="end">
								<IconButton size="small" onClick={() => onSearchChange('')} edge="end">
									<IconClose sx={{ fontSize: 18 }} />
								</IconButton>
							</InputAdornment>
						) : null,
					}}
				/>

				{/* Spacer */}
				<Box sx={{ flex: 1 }} />

				{/* Active filter chips - inline, show just the value */}
				{activeFilters.map((filter) => (
					<Chip
						key={filter.key}
						label={filter.value}
						onDelete={() => handleClearFilter(filter.key, filter.defaultValue)}
						size="small"
						sx={{
							backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
							color: 'primary.main',
							fontWeight: 500,
							height: 32,
							borderRadius: 1,
							'& .MuiChip-label': {
								px: 1.5,
							},
							'& .MuiChip-deleteIcon': {
								color: 'primary.main',
								fontSize: 18,
								mr: 0.5,
								'&:hover': {
									color: 'primary.dark',
								},
							},
						}}
					/>
				))}

				{/* Filters Button */}
				<Button
					onClick={handleOpenFilters}
					startIcon={<IconTune sx={{ fontSize: 18 }} />}
					variant={activeFilterCount > 0 ? 'contained' : 'outlined'}
					color={activeFilterCount > 0 ? 'primary' : 'inherit'}
					disableElevation
					sx={{
						textTransform: 'none',
						fontWeight: 500,
						borderRadius: 1,
						px: 2,
						py: 0.875,
						minHeight: 40,
						...(activeFilterCount === 0 && {
							borderColor: 'divider',
							color: 'text.secondary',
							'&:hover': {
								borderColor: 'text.secondary',
								backgroundColor: 'action.hover',
							},
						}),
					}}>
					{t('filters')}{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
				</Button>

				{/* Actions */}
				{actions}
			</Stack>

			{/* Filters Popover */}
			<Popover
				open={open}
				anchorEl={anchorEl}
				onClose={handleCloseFilters}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'right',
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'right',
				}}
				slotProps={{
					paper: {
						sx: {
							mt: 0.5,
							width: 300,
							boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
							borderRadius: 1.5,
						},
					},
				}}>
				<Box
					sx={{
						px: 2.5,
						py: 2,
						borderBottom: '1px solid',
						borderColor: 'divider',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
					}}>
					<Typography variant="subtitle2" fontWeight={600}>
						{t('filters')}
					</Typography>
					{activeFilterCount > 0 && (
						<Link
							component="button"
							variant="body2"
							onClick={handleClearAllFilters}
							underline="hover"
							sx={{ color: 'text.secondary', cursor: 'pointer' }}>
							{t('clearAll')}
						</Link>
					)}
				</Box>

				<Stack spacing={2.5} sx={{ p: 2.5 }}>
					{filters.map((filter) => (
						<Box key={filter.key}>
							<Typography variant="body2" fontWeight={500} sx={{ mb: 1, color: 'text.primary' }}>
								{filter.label}
							</Typography>
							<Select
								fullWidth
								size="small"
								value={filterValues[filter.key] ?? filter.options[0]?.value ?? ''}
								onChange={(e) => onFilterChange(filter.key, e.target.value)}
								sx={{
									backgroundColor: 'background.paper',
									'& .MuiSelect-select': {
										py: 1.25,
									},
								}}>
								{filter.options.map((option) => (
									<MenuItem key={option.value} value={option.value}>
										{option.label}
									</MenuItem>
								))}
							</Select>
						</Box>
					))}
				</Stack>
			</Popover>
		</Box>
	);
};

// Pagination Bar Component
interface PaginationBarProps {
	totalCount: number;
	page?: number;
	rowsPerPage?: number;
	onPageChange?: (page: number) => void;
	onRowsPerPageChange?: (rowsPerPage: number) => void;
	rowsPerPageOptions?: number[];
}

export const PaginationBar = ({
	totalCount,
	page = 1,
	rowsPerPage = 10,
	onPageChange,
	onRowsPerPageChange,
	rowsPerPageOptions = [10, 25, 50, 100],
}: PaginationBarProps) => {
	const { t } = useTranslation();
	const totalPages = Math.ceil(totalCount / rowsPerPage) || 1;
	const start = totalCount === 0 ? 0 : (page - 1) * rowsPerPage + 1;
	const end = Math.min(page * rowsPerPage, totalCount);

	return (
		<Box
			sx={{
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'space-between',
				py: 1.5,
			}}>
			{/* Combined results info on left */}
			<Box sx={{ color: 'text.secondary', fontSize: 14 }}>
				{t('paginationBar.showing', { count: totalCount, start, end, total: totalCount })}
			</Box>

			{/* Controls on right */}
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
				{/* Rows per page */}
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						gap: 1,
						color: 'text.secondary',
						fontSize: 14,
					}}>
					{t('rowsPerPage')}
					<Select
						value={rowsPerPage}
						onChange={(e) => onRowsPerPageChange?.(Number(e.target.value))}
						size="small"
						variant="outlined"
						sx={{
							fontSize: 14,
							'& .MuiSelect-select': { py: 0.5, pl: 1.5, pr: 4 },
							'& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
						}}>
						{rowsPerPageOptions.map((n) => (
							<MenuItem key={n} value={n} sx={{ fontSize: 14 }}>
								{n}
							</MenuItem>
						))}
					</Select>
				</Box>

				{/* Prev/Next buttons with page number */}
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
					<IconButton
						onClick={() => onPageChange?.(page - 1)}
						disabled={page <= 1}
						size="small"
						sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
						<IconChevronLeft />
					</IconButton>
					<Box
						sx={{
							minWidth: 32,
							height: 32,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							border: '1px solid',
							borderColor: 'divider',
							borderRadius: 1,
							fontSize: 14,
							color: 'text.primary',
						}}>
						{page}
					</Box>
					<IconButton
						onClick={() => onPageChange?.(page + 1)}
						disabled={page >= totalPages}
						size="small"
						sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
						<IconChevronRight />
					</IconButton>
				</Box>
			</Box>
		</Box>
	);
};
